import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";

/*
 * Copyright (C) 2017 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that they will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, { Component } from 'react';
import classnames from 'classnames';
import { themeable, ApplyTheme } from '@instructure/ui-themeable';
import { Text, Pill, Avatar } from '@instructure/ui-elements';
import { Checkbox, CheckboxFacade } from '@instructure/ui-checkbox';
import { ScreenReaderContent, PresentationContent } from '@instructure/ui-a11y';
import { Button } from '@instructure/ui-buttons';
import { IconAssignmentLine, IconQuizLine, IconAnnouncementLine, IconDiscussionLine, IconCalendarMonthLine, IconDocumentLine, IconEditLine, IconPeerReviewLine } from '@instructure/ui-icons';
import { arrayOf, bool, number, string, func, shape, object } from 'prop-types';
import { momentObj } from 'react-moment-proptypes'; // eslint-disable-next-line import/no-named-as-default

import NotificationBadge, { MissingIndicator, NewActivityIndicator } from '../NotificationBadge';
import BadgeList from '../BadgeList';
import CalendarEventModal from '../CalendarEventModal';
import responsiviser from '../responsiviser';
const styles = {
  componentId: 'dsRAF',
  template: function template(theme) {
    return "\n\n.PlannerItem-styles__root {\n  font-family: ".concat(theme.fontFamily || 'inherit', ";\n  box-sizing: border-box;\n  padding: ").concat(theme.padding || 'inherit', ";\n  border-bottom: ").concat(theme.borderWidth || 'inherit', " solid ").concat(theme.borderColor || 'inherit', ";\n  flex: 1;\n  display: flex;\n  align-items: center;\n  color: ").concat(theme.color || 'inherit', ";\n  line-height: ").concat(theme.lineHeight || 'inherit', ";\n}\n  .PlannerItem-styles__root.PlannerItem-styles__small {\n    align-items: flex-start;\n  }\n\n.PlannerItem-styles__completed,\n.PlannerItem-styles__avatar,\n.PlannerItem-styles__icon,\n.PlannerItem-styles__layout {\n  box-sizing: border-box;\n}\n\n.PlannerItem-styles__completed {\n  width: 1.375rem;\n  -webkit-margin-start: ").concat(theme.gutterWidth || 'inherit', ";\n          margin-inline-start: ").concat(theme.gutterWidth || 'inherit', ";\n}\n\n[dir=\"ltr\"] .PlannerItem-styles__completed {\n  margin-left: ").concat(theme.gutterWidth || 'inherit', ";\n}\n\n[dir=\"rtl\"] .PlannerItem-styles__completed {\n  margin-right: ").concat(theme.gutterWidth || 'inherit', ";\n}\n\n.PlannerItem-styles__activityIndicator {\n  -webkit-padding-end: 0;\n          padding-inline-end: 0;\n  -webkit-padding-start: 0;\n          padding-inline-start: 0;\n}\n\n[dir=\"ltr\"] .PlannerItem-styles__activityIndicator {\n  padding-right: 0;\n  padding-left: 0;\n}\n\n[dir=\"rtl\"] .PlannerItem-styles__activityIndicator {\n  padding-left: 0;\n  padding-right: 0;\n}\n\n.PlannerItem-styles__activityIndicator + .PlannerItem-styles__completed {\n  -webkit-margin-start: calc(").concat(theme.gutterWidth || 'inherit', " - ").concat(theme.activityIndicatorWidth || 'inherit', ");\n          margin-inline-start: calc(").concat(theme.gutterWidth || 'inherit', " - ").concat(theme.activityIndicatorWidth || 'inherit', ")\n}\n\n[dir=\"ltr\"] .PlannerItem-styles__activityIndicator + .PlannerItem-styles__completed {\n  margin-left: calc(").concat(theme.gutterWidth || 'inherit', " - ").concat(theme.activityIndicatorWidth || 'inherit', ")\n}\n\n[dir=\"rtl\"] .PlannerItem-styles__activityIndicator + .PlannerItem-styles__completed {\n  margin-right: calc(").concat(theme.gutterWidth || 'inherit', " - ").concat(theme.activityIndicatorWidth || 'inherit', ")\n}\n\n.PlannerItem-styles__icon {\n  color: ").concat(theme.iconColor || 'inherit', ";\n  margin: 0 ").concat(theme.gutterWidth || 'inherit', ";\n}\n\n.PlannerItem-styles__icon > svg { \n    display: block;\n  }\n\n.PlannerItem-styles__avatar {\n  \n  margin: 0 calc(").concat(theme.gutterWidth || 'inherit', " - ((1em*2.5) - ").concat(theme.iconFontSize || 'inherit', ")/2);\n}\n\n.PlannerItem-styles__layout {\n  display: flex;\n  flex-direction: column;\n  flex: 1 0;\n  min-width: 1px;\n}\n.PlannerItem-styles__innerLayout {\n  display: flex;\n  flex: 1 0;\n  align-items: center;\n  min-width: 1px;\n  min-height: 2.5rem; \n}\n\n.PlannerItem-styles__details {\n  flex: 0 0 50%;\n  margin-bottom: 0;\n  box-sizing: border-box;\n  min-width: 1px;\n}\n\n.PlannerItem-styles__details.PlannerItem-styles__details_no_badges {\n     flex: 0 0 75%;\n   }\n\n.PlannerItem-styles__secondary {\n  flex: 0 0 50%;\n  box-sizing: border-box;\n  display: flex;\n  align-items: center;\n  justify-content: flex-end;\n  min-width: 1px;\n}\n\n.PlannerItem-styles__secondary.PlannerItem-styles__secondary_no_badges {\n     flex: 0 0 25%;\n   }\n\n.PlannerItem-styles__type {\n  box-sizing: border-box;\n  line-height: 1;\n  text-transform: uppercase;\n  letter-spacing: 0.0625rem;\n  margin-bottom: ").concat(theme.typeMargin || 'inherit', ";\n}\n\n.PlannerItem-styles__title {\n  box-sizing: border-box;\n  line-height: ").concat(theme.titleLineHeight || 'inherit', ";\n}\n\n.PlannerItem-styles__metrics {\n  box-sizing: border-box;\n  text-align: end;\n  flex: 0 0 10rem;\n  min-width: 1px;\n  -webkit-padding-start: ").concat(theme.metricsPadding || 'inherit', ";\n          padding-inline-start: ").concat(theme.metricsPadding || 'inherit', ";\n}\n\n[dir=\"ltr\"] .PlannerItem-styles__metrics {\n  text-align: right;\n  padding-left: ").concat(theme.metricsPadding || 'inherit', ";\n}\n\n[dir=\"rtl\"] .PlannerItem-styles__metrics {\n  text-align: left;\n  padding-right: ").concat(theme.metricsPadding || 'inherit', ";\n}\n\n.PlannerItem-styles__metrics.PlannerItem-styles__with_end_time {\n    flex-basis: 14rem;\n  }\n\n.PlannerItem-styles__metrics.PlannerItem-styles__with_end_time .PlannerItem-styles__due {\n      text-transform: none;\n    }\n\n.PlannerItem-styles__due,\n.PlannerItem-styles__score {\n  box-sizing: border-box;\n  text-transform: uppercase;\n  letter-spacing: 0.0625rem;\n  line-height: 1;\n  white-space: nowrap;\n}\n\n.PlannerItem-styles__badges {\n  flex: 1;\n  text-align: end;\n  min-width: 1px;\n}\n\n[dir=\"ltr\"] .PlannerItem-styles__badges {\n  text-align: right;\n}\n\n[dir=\"rtl\"] .PlannerItem-styles__badges {\n  text-align: left;\n}\n\n.PlannerItem-styles__feedback {\n  display: flex;\n  align-items: center;\n  min-height: 40px;\n}\n\n\n\n.PlannerItem-styles__feedback .PlannerItem-styles__feedbackAvatar {\n    flex-shrink: 0;\n    -webkit-margin-end: ").concat(theme.gutterWidth || 'inherit', ";\n            margin-inline-end: ").concat(theme.gutterWidth || 'inherit', ";\n  }\n\n[dir=\"ltr\"] .PlannerItem-styles__feedback .PlannerItem-styles__feedbackAvatar {\n    margin-right: ").concat(theme.gutterWidth || 'inherit', ";\n  }\n\n[dir=\"rtl\"] .PlannerItem-styles__feedback .PlannerItem-styles__feedbackAvatar {\n    margin-left: ").concat(theme.gutterWidth || 'inherit', ";\n  }\n\n.PlannerItem-styles__feedback .PlannerItem-styles__feedbackComment {\n    overflow: hidden;\n    white-space: nowrap;\n    text-overflow: ellipsis;\n  }\n\n.PlannerItem-styles__small .PlannerItem-styles__title, .PlannerItem-styles__medium .PlannerItem-styles__title {\n    overflow: hidden;\n    white-space: nowrap;\n    text-overflow: ellipsis;\n    -webkit-padding-end: 0.5rem;\n            padding-inline-end: 0.5rem;\n  }\n\n[dir=\"ltr\"] .PlannerItem-styles__small .PlannerItem-styles__title,\n[dir=\"ltr\"] .PlannerItem-styles__medium .PlannerItem-styles__title {\n    padding-right: 0.5rem;\n  }\n\n[dir=\"rtl\"] .PlannerItem-styles__small .PlannerItem-styles__title,\n[dir=\"rtl\"] .PlannerItem-styles__medium .PlannerItem-styles__title {\n    padding-left: 0.5rem;\n  }\n\n.PlannerItem-styles__small {\n  padding-left: 0;\n  padding-right: 0;\n}\n\n.PlannerItem-styles__small .PlannerItem-styles__completed {\n    -webkit-margin-start: 6px;\n            margin-inline-start: 6px;\n  }\n\n[dir=\"ltr\"] .PlannerItem-styles__small .PlannerItem-styles__completed {\n    margin-left: 6px;\n  }\n\n[dir=\"rtl\"] .PlannerItem-styles__small .PlannerItem-styles__completed {\n    margin-right: 6px;\n  }\n\n.PlannerItem-styles__small .PlannerItem-styles__innerLayout {\n    flex-direction: column;\n    align-items:flex-start;\n    -webkit-margin-start: 1rem;\n            margin-inline-start: 1rem;\n  }\n\n[dir=\"ltr\"] .PlannerItem-styles__small .PlannerItem-styles__innerLayout {\n    margin-left: 1rem;\n  }\n\n[dir=\"rtl\"] .PlannerItem-styles__small .PlannerItem-styles__innerLayout {\n    margin-right: 1rem;\n  }\n\n.PlannerItem-styles__small .PlannerItem-styles__details {\n    margin-bottom: 1rem;\n    flex: 1 0 auto;\n    width: 100%\n  }\n\n.PlannerItem-styles__small .PlannerItem-styles__secondary {\n    flex: 1 0 auto;\n    width: 100%;\n  }\n\n.PlannerItem-styles__small .PlannerItem-styles__metrics {\n    display: flex;\n    flex-direction: row-reverse;\n    justify-content: space-between;\n    align-items: flex-end;\n    flex: 1 0 auto;\n    text-align: unset;\n    -webkit-padding-start: 0;\n            padding-inline-start: 0;\n  }\n\n[dir=\"ltr\"] .PlannerItem-styles__small .PlannerItem-styles__metrics {\n    text-align: unset;\n    padding-left: 0;\n  }\n\n[dir=\"rtl\"] .PlannerItem-styles__small .PlannerItem-styles__metrics {\n    text-align: unset;\n    padding-right: 0;\n  }\n\n.PlannerItem-styles__small .PlannerItem-styles__due {\n    width: 100%;\n  }\n\n.PlannerItem-styles__small .PlannerItem-styles__icon, .PlannerItem-styles__small .PlannerItem-styles__avatar, .PlannerItem-styles__small .PlannerItem-styles__badges, .PlannerItem-styles__small .PlannerItem-styles__feedback, .PlannerItem-styles__small .PlannerItem-styles__editButton {\n    display: none;\n  }\n\n.PlannerItem-styles__small .PlannerItem-styles__location {\n    -webkit-margin-start: 1rem;\n            margin-inline-start: 1rem;\n  }\n\n[dir=\"ltr\"] .PlannerItem-styles__small .PlannerItem-styles__location {\n    margin-left: 1rem;\n  }\n\n[dir=\"rtl\"] .PlannerItem-styles__small .PlannerItem-styles__location {\n    margin-right: 1rem;\n  }\n");
  },
  'root': 'PlannerItem-styles__root',
  'small': 'PlannerItem-styles__small',
  'completed': 'PlannerItem-styles__completed',
  'avatar': 'PlannerItem-styles__avatar',
  'icon': 'PlannerItem-styles__icon',
  'layout': 'PlannerItem-styles__layout',
  'activityIndicator': 'PlannerItem-styles__activityIndicator',
  'innerLayout': 'PlannerItem-styles__innerLayout',
  'details': 'PlannerItem-styles__details',
  'details_no_badges': 'PlannerItem-styles__details_no_badges',
  'secondary': 'PlannerItem-styles__secondary',
  'secondary_no_badges': 'PlannerItem-styles__secondary_no_badges',
  'type': 'PlannerItem-styles__type',
  'title': 'PlannerItem-styles__title',
  'metrics': 'PlannerItem-styles__metrics',
  'with_end_time': 'PlannerItem-styles__with_end_time',
  'due': 'PlannerItem-styles__due',
  'score': 'PlannerItem-styles__score',
  'badges': 'PlannerItem-styles__badges',
  'feedback': 'PlannerItem-styles__feedback',
  'feedbackAvatar': 'PlannerItem-styles__feedbackAvatar',
  'feedbackComment': 'PlannerItem-styles__feedbackComment',
  'medium': 'PlannerItem-styles__medium',
  'editButton': 'PlannerItem-styles__editButton',
  'location': 'PlannerItem-styles__location'
};
import theme from './theme.js';
import { badgeShape, userShape, statusShape, sizeShape, feedbackShape } from '../plannerPropTypes';
import { showPillForOverdueStatus } from '../../utilities/statusUtils';
import formatMessage from '../../format-message';
import { animatable } from '../../dynamic-ui';
export class PlannerItem extends Component {
  constructor(props) {
    super(props);

    this.toDoLinkClick = e => {
      e.preventDefault();
      this.props.updateTodo({
        updateTodoItem: _objectSpread({}, this.props)
      });
    };

    this.registerRootDivRef = elt => {
      this.rootDivRef = elt;
    };

    this.registerFocusElementRef = elt => {
      this.checkboxRef = elt;
    };

    this.getFocusable = which => {
      return which === 'update' || which === 'delete' ? this.itemLink : this.checkboxRef;
    };

    this.renderDateField = () => {
      if (this.props.date) {
        if (this.props.allDay) {
          return formatMessage('All Day');
        }

        if (this.props.associated_item === 'Calendar Event') {
          if (this.showEndTime()) {
            return formatMessage('{startTime} to {endTime}', {
              startTime: this.props.date.format('LT'),
              endTime: this.props.endTime.format('LT')
            });
          } else {
            return formatMessage(this.props.date.format('LT'));
          }
        }

        if (this.hasDueTime()) {
          if (this.props.associated_item === 'Peer Review') {
            return formatMessage('Reminder: {date}', {
              date: this.props.date.format('LT')
            });
          } else if (this.props.dateStyle === 'todo') {
            return formatMessage('To Do: {date}', {
              date: this.props.date.format('LT')
            });
          } else {
            return formatMessage('Due: {date}', {
              date: this.props.date.format('LT')
            });
          }
        }

        return this.props.date.format('LT');
      }

      return null;
    };

    this.openCalendarEventModal = () => {
      this.setState({
        calendarEventModalOpen: true
      });
    };

    this.closeCalendarEventModal = () => {
      this.setState({
        calendarEventModalOpen: false
      });
    };

    this.renderIcon = () => {
      const currentUser = this.props.currentUser || {};

      switch (this.props.associated_item) {
        case 'Assignment':
          return React.createElement(IconAssignmentLine, null);

        case 'Quiz':
          return React.createElement(IconQuizLine, null);

        case 'Discussion':
          return React.createElement(IconDiscussionLine, null);

        case 'Announcement':
          return React.createElement(IconAnnouncementLine, null);

        case 'Calendar Event':
          return React.createElement(IconCalendarMonthLine, null);

        case 'Page':
          return React.createElement(IconDocumentLine, null);

        case 'Peer Review':
          return React.createElement(IconPeerReviewLine, null);

        default:
          return React.createElement(Avatar, {
            name: currentUser.displayName || '?',
            src: currentUser.avatarUrl,
            size: "small",
            "data-fs-exclude": true
          });
      }
    };

    this.renderBadges = () => {
      if (this.props.badges.length) {
        return React.createElement(BadgeList, null, this.props.badges.map(b => React.createElement(Pill, {
          key: b.id,
          text: b.text,
          variant: b.variant
        })));
      }

      return null;
    };

    this.renderItemSubMetric = () => {
      if (this.props.points) {
        return React.createElement("div", {
          className: styles.score
        }, React.createElement(Text, {
          color: "secondary"
        }, React.createElement(Text, {
          size: "large"
        }, this.props.points), React.createElement(Text, {
          size: "x-small"
        }, "\xA0", formatMessage('pts'))));
      }

      if (this.props.associated_item === 'To Do') {
        return React.createElement("div", {
          className: styles.editButton
        }, React.createElement(ApplyTheme, {
          theme: {
            [Button.theme]: {
              iconColor: this.props.color
            }
          }
        }, React.createElement(Button, {
          variant: "icon",
          icon: IconEditLine,
          onClick: this.toDoLinkClick
        }, React.createElement(ScreenReaderContent, null, formatMessage('Edit')))));
      }

      return null;
    };

    this.renderItemMetrics = () => {
      const secondaryClasses = classnames(styles.secondary, !this.hasBadges() ? styles.secondary_no_badges : '');
      const metricsClasses = classnames(styles.metrics, {
        [styles.with_end_time]: this.showEndTime()
      });
      return React.createElement("div", {
        className: secondaryClasses
      }, React.createElement("div", {
        className: styles.badges
      }, this.renderBadges()), React.createElement("div", {
        className: metricsClasses
      }, this.renderItemSubMetric(), React.createElement("div", {
        className: styles.due
      }, React.createElement(Text, {
        color: "secondary",
        size: "x-small"
      }, React.createElement(PresentationContent, null, this.renderDateField())))));
    };

    this.renderType = () => {
      if (!this.props.associated_item) {
        return formatMessage('{course} TO DO', {
          course: this.props.courseName || ''
        });
      } else {
        return "".concat(this.props.courseName || '', " ").concat(this.assignmentType());
      }
    };

    this.renderItemDetails = () => {
      return React.createElement("div", {
        className: classnames(styles.details, !this.hasBadges() ? styles.details_no_badges : '')
      }, React.createElement("div", {
        className: styles.type
      }, React.createElement(Text, {
        size: "x-small",
        color: "secondary"
      }, this.renderType())), this.renderTitle());
    };

    this.getCheckboxTheme = () => {
      return {
        checkedBackground: this.props.color,
        checkedBorderColor: this.props.color,
        borderColor: this.props.color,
        hoverBorderColor: this.props.color
      };
    };

    this.state = {
      calendarEventModalOpen: false,
      completed: props.completed
    };
  }

  componentDidMount() {
    this.props.registerAnimatable('item', this, this.props.animatableIndex, [this.props.uniqueId]);
  }

  componentWillReceiveProps(nextProps) {
    this.props.deregisterAnimatable('item', this, [this.props.uniqueId]);
    this.props.registerAnimatable('item', this, nextProps.animatableIndex, [nextProps.uniqueId]);
    this.setState({
      completed: nextProps.completed
    });
  }

  componentWillUnmount() {
    this.props.deregisterAnimatable('item', this, [this.props.uniqueId]);
  }

  getScrollable() {
    return this.rootDivRef;
  }

  getLayout() {
    return this.props.responsiveSize;
  }

  hasDueTime() {
    return this.props.date && !(this.props.associated_item === 'Announcement' || this.props.associated_item === 'Calendar Event');
  }

  showEndTime() {
    return this.props.date && !this.props.allDay && this.props.endTime && !this.props.endTime.isSame(this.props.date);
  }

  hasBadges() {
    return this.props.badges && this.props.badges.length && this.props.badges.length > 0;
  }

  assignmentType() {
    switch (this.props.associated_item) {
      case 'Quiz':
        return formatMessage('Quiz');

      case 'Discussion':
        return formatMessage('Discussion');

      case 'Assignment':
        return formatMessage('Assignment');

      case 'Page':
        return formatMessage('Page');

      case 'Announcement':
        return formatMessage('Announcement');

      case 'To Do':
        return formatMessage('To Do');

      case 'Calendar Event':
        return formatMessage('Calendar Event');

      case 'Peer Review':
        return formatMessage('Peer Review');

      default:
        return formatMessage('Task');
    }
  }

  linkLabel() {
    const assignmentType = this.assignmentType();
    const datetimeformat = this.props.allDay === true ? 'LL' : 'LLLL';
    const params = {
      assignmentType,
      title: this.props.title,
      datetime: this.props.date ? this.props.date.format(datetimeformat) : null
    };

    if (this.props.date) {
      if (this.props.allDay) {
        return formatMessage('{assignmentType} {title}, all day on {datetime}.', params);
      }

      if (this.props.associated_item === 'Calendar Event') {
        if (this.showEndTime()) {
          params.endTime = this.props.endTime.format('LT');
          return formatMessage('{assignmentType} {title}, at {datetime} until {endTime}', params);
        } else {
          return formatMessage('{assignmentType} {title}, at {datetime}.', params);
        }
      }

      if (this.hasDueTime()) {
        if (this.props.dateStyle === 'todo') {
          return formatMessage('{assignmentType} {title} has a to do time at {datetime}.', params);
        } else if (this.props.associated_item === 'Peer Review') {
          return formatMessage('{assignmentType} {title}, reminder {datetime}.', params);
        } else {
          return formatMessage('{assignmentType} {title}, due {datetime}.', params);
        }
      }

      if (this.props.associated_item === 'Announcement') {
        return formatMessage('{assignmentType} {title} posted {datetime}.', params);
      }
    }

    return formatMessage('{assignmentType} {title}.', params);
  }

  renderCalendarEventModal() {
    if (this.props.associated_item !== 'Calendar Event') return null;
    return React.createElement(CalendarEventModal, {
      open: this.state.calendarEventModalOpen,
      requestClose: this.closeCalendarEventModal,
      title: this.props.title,
      html_url: this.props.html_url,
      courseName: this.props.courseName,
      currentUser: this.props.currentUser,
      location: this.props.location,
      address: this.props.address,
      details: this.props.details,
      startTime: this.props.date,
      endTime: this.props.endTime,
      allDay: !!this.props.allDay,
      timeZone: this.props.timeZone
    });
  }

  renderTitle() {
    const linkProps = {};

    if (this.props.associated_item === 'To Do') {
      linkProps.onClick = this.toDoLinkClick;
    }

    if (this.props.associated_item === 'Calendar Event') {
      linkProps.onClick = this.openCalendarEventModal;
    } else {
      linkProps.href = this.props.html_url;
    }

    return React.createElement("div", {
      className: styles.title,
      style: {
        position: 'relative'
      }
    }, React.createElement(Button, Object.assign({
      variant: "link",
      theme: {
        mediumPadding: '0',
        mediumHeight: 'normal'
      },
      buttonRef: link => {
        this.itemLink = link;
      }
    }, linkProps), React.createElement(ScreenReaderContent, null, this.linkLabel()), React.createElement(PresentationContent, null, this.props.title)), this.renderCalendarEventModal());
  }

  renderNotificationBadge() {
    if (!this.props.showNotificationBadge) {
      return null;
    }

    const newItem = this.props.newActivity;
    let missing = false;

    if (showPillForOverdueStatus('missing', {
      status: this.props.status,
      context: this.props.context
    })) {
      missing = true;
    }

    if (newItem || missing) {
      const IndicatorComponent = newItem ? NewActivityIndicator : MissingIndicator;
      return React.createElement(NotificationBadge, {
        responsiveSize: this.props.responsiveSize
      }, React.createElement("div", {
        className: styles.activityIndicator
      }, React.createElement(IndicatorComponent, {
        title: this.props.title,
        itemIds: [this.props.uniqueId],
        animatableIndex: this.props.animatableIndex,
        getFocusable: this.getFocusable
      })));
    } else {
      return React.createElement(NotificationBadge, {
        responsiveSize: this.props.responsiveSize
      });
    }
  }

  renderExtraInfo() {
    const feedback = this.props.feedback;

    if (feedback) {
      const comment = feedback.is_media ? formatMessage('You have media feedback.') : feedback.comment;
      return React.createElement("div", {
        className: styles.feedback
      }, React.createElement("span", {
        className: styles.feedbackAvatar
      }, React.createElement(Avatar, {
        name: feedback.author_name || '?',
        src: feedback.author_avatar_url,
        size: "small",
        "data-fs-exclude": true
      })), React.createElement("span", {
        className: styles.feedbackComment
      }, React.createElement(Text, {
        fontStyle: "italic"
      }, comment)));
    }

    const location = this.props.location;

    if (location) {
      return React.createElement("div", {
        className: styles.location
      }, React.createElement(Text, {
        color: "secondary"
      }, location));
    }

    return null;
  }

  render() {
    const assignmentType = this.assignmentType();
    const checkboxLabel = this.state.completed ? formatMessage('{assignmentType} {title} is marked as done.', {
      assignmentType,
      title: this.props.title
    }) : formatMessage('{assignmentType} {title} is not marked as done.', {
      assignmentType,
      title: this.props.title
    });
    return React.createElement("div", {
      className: classnames(styles.root, styles[this.getLayout()], 'planner-item'),
      ref: this.registerRootDivRef
    }, this.renderNotificationBadge(), React.createElement("div", {
      className: styles.completed
    }, React.createElement(ApplyTheme, {
      theme: {
        [CheckboxFacade.theme]: this.getCheckboxTheme()
      }
    }, React.createElement(Checkbox, {
      ref: this.registerFocusElementRef,
      label: React.createElement(ScreenReaderContent, null, checkboxLabel),
      checked: this.props.toggleAPIPending ? !this.state.completed : this.state.completed,
      onChange: this.props.toggleCompletion,
      disabled: this.props.toggleAPIPending
    }))), React.createElement("div", {
      className: this.props.associated_item === 'To Do' ? styles.avatar : styles.icon,
      style: {
        color: this.props.color
      },
      "aria-hidden": "true"
    }, this.renderIcon()), React.createElement("div", {
      className: styles.layout
    }, React.createElement("div", {
      className: styles.innerLayout
    }, this.renderItemDetails(), this.renderItemMetrics()), this.renderExtraInfo()));
  }

}
PlannerItem.propTypes = {
  color: string,
  id: string.isRequired,
  uniqueId: string.isRequired,
  animatableIndex: number,
  title: string.isRequired,
  points: number,
  date: momentObj,
  dateStyle: string,
  details: string,
  courseName: string,
  completed: bool,
  overrideId: string,
  associated_item: string,
  context: object,
  html_url: string,
  toggleCompletion: func,
  updateTodo: func.isRequired,
  badges: arrayOf(shape(badgeShape)),
  registerAnimatable: func,
  deregisterAnimatable: func,
  toggleAPIPending: bool,
  status: statusShape,
  newActivity: bool,
  showNotificationBadge: bool,
  currentUser: shape(userShape),
  responsiveSize: sizeShape,
  allDay: bool,
  feedback: shape(feedbackShape),
  location: string,
  endTime: momentObj,
  timeZone: string.isRequired
};
PlannerItem.defaultProps = {
  badges: [],
  responsiveSize: 'large',
  allDay: false
};
const ResponsivePlannerItem = responsiviser()(PlannerItem);
export default animatable(themeable(theme, styles)(ResponsivePlannerItem));